import { Box, Container, Stack } from "@mui/material";
import { Outlet, useSearchParams, useLocation } from "react-router-dom";
import Footer from "@components/Footer";
import Header from "@components/Header";
import Cart from "@components/Cart";
import { useForm, FormProvider } from "react-hook-form";
import type { FormDefaults as FormDefaultsInterface } from "@constants/formDefaults";
import { formDefaults } from "@constants/formDefaults";
import { useEffect } from "react";
import { parseCurrencyFromURL } from "@utils/currencyUtils";
import { useToken } from "@contexts/TokenContext";
import { PRODUCTS } from "@constants/prices";
import { useGetMe } from "@queries/private/useGetMe";
import { useGetOrganization } from "@queries/private/useGetOrganization";
import { PurchaseFlowProvider } from "@contexts/PurchaseFlowContext";
import { CartInfoProvider } from "@contexts/CartInfoContext";

export default function StoreLayout() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const methods = useForm<FormDefaultsInterface>({ defaultValues: formDefaults });
  const skus = searchParams.get("skus")?.split(",").filter(Boolean) ?? [];
  const redirectUrl = searchParams.get("redirectUrl") ?? "";

  // Parse currency from URL on initial load
  const urlCurrency = parseCurrencyFromURL(searchParams);

  // Authentication hooks - conditionally used based on token presence
  const { idToken, isAuthenticated } = useToken();
  const { data: userData, isLoading: userLoading, refetch: refetchUser } = useGetMe();
  const { refetch: refetchOrganization } = useGetOrganization();

  const allowedRedirectOrigins = new Set(["https://integratedlistening.com", "https://my.unyte.com"]);

  const isRedirectUrlValid = (): boolean => {
    try {
      const url = new URL(redirectUrl);
      return allowedRedirectOrigins.has(url.origin);
    } catch {
      return false; // Invalid URL format
    }
  };

  // Trigger user data fetch and purchase creation when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log("StoreLayout: Processing authenticated user with SKUs:", skus);
      const fetchAll = async () => {
        const result = await refetchUser();
        if (result.data) {
          const data = result.data;
          // Set firstName (directly available from API)
          methods.setValue("firstName", data.firstName || "" as any);

          // Set lastName (directly available from API)
          methods.setValue("lastName", data.lastName || "" as any);

          // Set email
          methods.setValue("email", data.email || "" as any);

          // Set organizationName from organization object
          if (data.organization?.name) {
            methods.setValue("organizationName", data.organization.name as any);
          }

          // Set professionalCredentials if we have otherProfessionalCredentials
          if (data.otherProfessionalCredentials) {
            methods.setValue("professionalCredentials", "Other" as any);
          }
        }

        // Fetch organization data and set product statuses
        const orgResult = await refetchOrganization();
        console.log("org?", { orgResult });
        if (orgResult.data?.productsStatus) {
          console.log("StoreLayout: Setting organization products:", orgResult.data.productsStatus);
          methods.setValue("organizationProducts", orgResult.data.productsStatus as any);
        }
      };

      if (idToken && !userData && !userLoading) {
        fetchAll();
      }
    }
  }, [isAuthenticated, idToken, userData, userLoading, refetchUser, refetchOrganization, methods]);

  // Set currency from URL parameter
  useEffect(() => {
    const currentCurrency = methods.getValues("selectedCurrency");
    if (urlCurrency !== currentCurrency) {
      methods.setValue("selectedCurrency", urlCurrency as any);
    }
  }, [urlCurrency, methods]);

  // Initialize cart with SKUs from URL
  useEffect(() => {
    if (skus.length > 0) {
      if (isAuthenticated) {
        console.log("StoreLayout: Processing SKUs from URL:", skus);
      }

      const currentItems = methods.getValues("items") || [];
      const existingSkus = new Set(currentItems.map((item: any) => item.sku));

      const newItems = skus.filter((sku) => !existingSkus.has(sku)).map((sku) => ({ sku, quantity: 1 }));

      if (newItems.length > 0) {
        const allItems = [...currentItems, ...newItems];
        methods.setValue("items", allItems as any);
        if (isAuthenticated) {
          console.log("StoreLayout: Cart items set:", allItems);
        }
      }

      // Extract categories from PRODUCTS for step flow determination
      const categorySet = new Set<string>();
      const accessoryCategorySet = new Set<string>();

      for (const sku of skus) {
        const product = PRODUCTS[sku];
        if (product?.productCategories) {
          for (const cat of product.productCategories) {
            categorySet.add(cat);
          }
        }
        if (product?.accessoryCategories) {
          for (const cat of product.accessoryCategories) {
            accessoryCategorySet.add(cat);
          }
        }
      }

      const combinedCategories = Array.from(categorySet);
      const combinedAccessoryCategories = Array.from(accessoryCategorySet);

      if (combinedCategories.length > 0) {
        methods.setValue("productCategories", combinedCategories as any);
        if (isAuthenticated) {
          console.log("StoreLayout: Product categories set:", combinedCategories);
        }
      }
      if (combinedAccessoryCategories.length > 0) {
        methods.setValue("accessoryCategories", combinedAccessoryCategories as any);
        if (isAuthenticated) {
          console.log("StoreLayout: Accessory categories set:", combinedAccessoryCategories);
        }
      }
    } else if (isAuthenticated) {
      console.log("StoreLayout: No SKUs provided in URL");
    }

    // Set redirect URL if valid
    if (isRedirectUrlValid()) {
      methods.setValue("redirectUrl", redirectUrl as any);
      if (isAuthenticated) {
        console.log("StoreLayout: Redirect URL set:", redirectUrl);
      }
    }
  }, [isAuthenticated]);

  // Determine if this is a mockup route
  const isMockup = location.pathname.startsWith("/mockup");

  return (
    <FormProvider {...methods}>
      <CartInfoProvider>
        <PurchaseFlowProvider isMockup={isMockup}>
          <Box
            display="flex"
            flexDirection="column"
            minHeight="100vh"
            minWidth="100vw"
            sx={{ backgroundColor: "#eef7f3" }}
          >
            {!isAuthenticated && <Header />}
            <Container maxWidth="xl" sx={{ flex: 1, py: "24px", height: "100%" }}>
              <Stack
                direction={{ xs: "column", md: "row-reverse" }}
                spacing={4}
                alignItems="flex-start"
              >
                <Box width={{ xs: "100%", md: "auto" }} flex={2} minWidth={{ md: "280px" }}>
                  <Cart />
                </Box>

                {/* Page Content */}
                <Box width={{ xs: "100%", md: "auto" }} flex={{ md: 3 }}>
                  <Outlet />
                </Box>
              </Stack>
            </Container>
            <Footer />
          </Box>
        </PurchaseFlowProvider>
      </CartInfoProvider>
    </FormProvider>
  );
}
